import { Controller, useForm } from "react-hook-form";
import React, { useContext, useState } from "react";
import {
  DELETE_ORGANIZATION,
  type DeleteOrganizationData,
  type DeleteOrganizationVariables,
} from "../CustomerProvisioningApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import {
  TextButton,
  TextInput,
  useMutationWrapper,
  AlertIndicator,
  alertTypes,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

interface DeleteOrganizationModalProps {
  organizationSlug: string;
  organizationName: string;
}

interface DeleteOrganizationFormData {
  deleteText: string;
}

const DeleteOrganizationModal: React.FC<DeleteOrganizationModalProps> = ({
  organizationSlug,
  organizationName,
}) => {
  const { closeModal } = useContext(ModalContext);

  const methods = useForm<DeleteOrganizationFormData>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = methods;
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const [deleteOrganization] = useMutationWrapper<DeleteOrganizationData>(
    DELETE_ORGANIZATION,
    {
      refetchQueries: ["cnc_organizations"],
    }
  );

  const onSubmit = () => {
    setIsSubmittingForm(true);

    deleteOrganization({
      variables: {
        organization_slug: organizationSlug,
      } as DeleteOrganizationVariables,
    })
      .then((res) => {
        if (!!res?.data?.cnc_delete_organization?.ok) {
          closeModal();
          showToast({ message: res?.data?.cnc_delete_organization?.msg });
        } else {
          setIsSubmittingForm(false);
          showErrorDialog({ message: res.data?.cnc_delete_organization?.msg });
        }
      })
      .catch((err: { message?: string }) => {
        setIsSubmittingForm(false);
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <div>
      <AlertIndicator
        type={alertTypes.WARNING}
        wrapperClassName={"connection-alert"}
        title={`This action cannot be undone`}
        content={
          <div>
            This will permanently delete organization{" "}
            <strong>{organizationName}</strong>.
          </div>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={"deleteText"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"delete-text"}
                labelTop
                onBlur={onBlur}
                name={"deleteText"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                }}
                label={`To confirm PERMANENT DELETION of the organization, please type the organization name below:`}
                required
                error={errors.deleteText ? errors.deleteText : null}
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: (value) => {
              return (
                value === organizationName || "Organization name is incorrect"
              );
            },
          }}
        />
        <ModalButtonsContainer>
          <TextButton
            type="submit"
            label={"Delete Organization"}
            isSecondary
            isDanger
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isSubmittingForm}
            disabled={!isValid || !isDirty || isSubmittingForm}
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default DeleteOrganizationModal;
